(() => {
  const SELECTOR = '[data-insieme-carousel-hint]';
  const MOBILE_MAX = 749;

  const easeInOut = (value) =>
    value < 0.5 ? 2 * value * value : 1 - Math.pow(-2 * value + 2, 2) / 2;

  const setup = (track) => {
    if (track.dataset.hintReady === 'true') return;
    track.dataset.hintReady = 'true';

    let animationFrame = 0;
    let visibilityFrame = 0;
    let timer = 0;
    let isPlaying = false;
    let hasPlayed = false;
    let userCancelled = false;
    let startScroll = 0;
    let previousSnapType = '';

    const canAnimate = () =>
      window.innerWidth <= MOBILE_MAX &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      track.scrollWidth > track.clientWidth + 8;

    const isInView = () => {
      const rect = track.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top <= viewportHeight * 0.78 && rect.bottom >= viewportHeight * 0.22;
    };

    const restoreSnap = () => {
      track.style.scrollSnapType = previousSnapType;
    };

    const stopAttempt = () => {
      window.clearTimeout(timer);
      window.cancelAnimationFrame(animationFrame);
      timer = 0;
      animationFrame = 0;

      if (isPlaying) {
        track.scrollLeft = startScroll;
        restoreSnap();
      }

      isPlaying = false;
    };

    const removeWindowListeners = () => {
      window.removeEventListener('scroll', queueVisibilityCheck);
      window.removeEventListener('resize', queueVisibilityCheck);
      window.removeEventListener('orientationchange', queueVisibilityCheck);
    };

    const cancelByUser = () => {
      userCancelled = true;
      stopAttempt();
      removeWindowListeners();
    };

    const animate = () => {
      if (userCancelled || hasPlayed || !canAnimate() || !isInView()) return;

      startScroll = track.scrollLeft;
      if (startScroll > 4) return;

      const requestedDistance = Number(track.dataset.hintDistance) || 36;
      const distance = Math.min(requestedDistance, track.scrollWidth - track.clientWidth);
      const duration = Number(track.dataset.hintDuration) || 1200;
      const startedAt = performance.now();

      previousSnapType = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';
      isPlaying = true;

      const tick = (now) => {
        if (userCancelled || !isInView()) {
          stopAttempt();
          return;
        }

        const progress = Math.min((now - startedAt) / duration, 1);
        const wave = progress <= 0.5 ? progress * 2 : (1 - progress) * 2;
        track.scrollLeft = startScroll + distance * easeInOut(wave);

        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(tick);
          return;
        }

        track.scrollLeft = startScroll;
        restoreSnap();
        isPlaying = false;
        hasPlayed = true;
        track.dataset.hintPlayed = 'true';
        removeWindowListeners();
      };

      animationFrame = window.requestAnimationFrame(tick);
    };

    const checkVisibility = () => {
      visibilityFrame = 0;
      if (userCancelled || hasPlayed) return;

      if (!canAnimate() || !isInView()) {
        stopAttempt();
        return;
      }

      if (isPlaying || timer) return;
      const delay = Number(track.dataset.hintDelay) || 0;
      timer = window.setTimeout(() => {
        timer = 0;
        animate();
      }, delay);
    };

    function queueVisibilityCheck() {
      if (visibilityFrame) return;
      visibilityFrame = window.requestAnimationFrame(checkVisibility);
    }

    const stopIfTouchedDuringAnimation = () => {
      if (isPlaying) cancelByUser();
    };

    const detectManualScroll = () => {
      if (!isPlaying && track.scrollLeft > 4) cancelByUser();
    };

    track.addEventListener('pointerdown', stopIfTouchedDuringAnimation, { passive: true });
    track.addEventListener('touchstart', stopIfTouchedDuringAnimation, { passive: true });
    track.addEventListener('scroll', detectManualScroll, { passive: true });

    window.addEventListener('scroll', queueVisibilityCheck, { passive: true });
    window.addEventListener('resize', queueVisibilityCheck, { passive: true });
    window.addEventListener('orientationchange', queueVisibilityCheck, { passive: true });

    queueVisibilityCheck();
  };

  const init = (root = document) => root.querySelectorAll(SELECTOR).forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
